/* eslint-disable react/react-in-jsx-scope */
import React, { useEffect, useState } from "react";
import { Box, Paper, IconButton, Typography } from "@mui/material";
import CloseIcon from "../../../assets/icons/cross-icon.svg";
import "react-phone-input-2/lib/style.css";
import GraceBot from "../../../assets/icons/grace-mascot.png";
import "./chatbot-component.scss";
import SendActiveIcon from "../../../assets/icons/send-icon-active.svg";
import SendInactiveIcon from "../../../assets/icons/send-icon-inactive.svg";
import TriangleIcon from "../../../assets/icons/triangle-icon.svg";
import { requestCall, requestMessage } from "../../../api/bot";
import NewPhoneNumberComponent from "../new-phone-input-component/new-phone-input-component";
import ChatBubble from "../chat-bubble/chat-bubble";
import { getGreeting } from "../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";

export default function ChatbotComponent() {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [loading, setLoading] = useState(false);

  const [joinTime, setJoinTime] = useState<string>("");

  const [messages, setMessages] = useState<any[]>([]);
  const [inputMode, setInputMode] = useState<"text" | "phone">("text");
  const [text, setText] = useState<string>(""); // store text input value
  const [awaitingCallConfirmation, setAwaitingCallConfirmation] =
    useState(false);
  const sessionRef = React.useRef(0);
  const [botTyping, setBotTyping] = useState(false);
  const [conversationLocked, setConversationLocked] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const { openChatBot, searchText } = useSelector((state: any) => state.search);
  const dispatch = useDispatch();

  // ref for scroll msg to bottom
  const chatBodyRef = React.useRef<HTMLDivElement | null>(null);

  const isTextValid = text.trim().length > 0;
  const isPhoneValid = phone.length === 10;

  const isInputDisabled =
    loading || conversationLocked || botTyping || isInitializing;

  const isSendDisabled =
    loading ||
    botTyping ||
    (inputMode === "text" && !isTextValid) ||
    (inputMode === "phone" && !isPhoneValid);

  function maskPhoneNumber(number: string): string {
    // ensure only digits and max 10
    const clean = number.replace(/\D/g, "").slice(0, 10);
    // fallback if not 10 digits
    if (clean.length !== 10) return number;

    const lastFour = clean.slice(6); // last 4 digits
    return `XXX-XXX-${lastFour}`;
  }

  const getCurrentTime = () => {
    const now = new Date();

    return now
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };
  // Add options as a new bot message
  const addBotOptions = async (text: string, options: string[], type?: string) => {
    const currentSession = sessionRef.current;
    const tempId = Math.random().toString(36).substr(2, 9);
    setBotTyping(true);
    // Push typing indicator first
    setMessages((prev) => [
      ...prev,
      { id: tempId, sender: "bot", isTyping: true },
    ]);

    // simulate typing
    await delay(2000);

    if (sessionRef.current !== currentSession) return;
    // Replace typing indicator with options message
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === tempId
          ? {
            id: tempId,
            sender: "bot",
            text,
            options,
            type,
            timestamp: getCurrentTime(),
          }
          : msg,
      ),
    );
    setBotTyping(false);
  };

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const addBotMessage = async (text: string) => {
    const currentSession = sessionRef.current;
    const tempId = Math.random().toString(36).substr(2, 9);
    setBotTyping(true);
    // Push temporary typing indicator
    setMessages((prev) => [
      ...prev,
      { id: tempId, sender: "bot", isTyping: true },
    ]);

    // Wait 2 seconds (simulate typing)
    await delay(2000);

    if (sessionRef.current !== currentSession) return;
    // Replace typing indicator with actual message
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === tempId
          ? { text, sender: "bot", timestamp: getCurrentTime() }
          : msg,
      ),
    );
    setBotTyping(false);
  };

  const handleSend = async () => {
    if (!phone.trim()) return;
    setConversationLocked(true);
    setLoading(true);
    setPhone("");

    const userTime = getCurrentTime();

    setMessages((prev) => [
      ...prev,
      {
        text: phone,
        sender: "user",
        timestamp: userTime,
      },
    ]);

    const formattedPhone = {
      phone_number: `${countryCode}${phone.replace(/\D/g, "").replace(countryCode, "")}`,
    };
    try {
      const response = await requestCall(formattedPhone);
      const status = response.status;
      let botMessage = "";

      switch (status) {
        case 200:
          botMessage =
            "Thank you for sharing your number. We're initiating a call now.";
          break;
        case 400:
          botMessage = "Something went wrong. Please retry.";
          break;
        case 403:
          botMessage =
            "You've discovered a beta feature! It's currently available to selected users, but we're working on making it available to everyone soon.";
          break;
        default:
          botMessage = `Please verify phone number or country code and retry.`;
          break;
      }
      await addBotMessage(botMessage);
      if (status === 200) {
        const currentSession = sessionRef.current;
        setTimeout(async () => {
          if (sessionRef.current !== currentSession) return;
          setInputMode("text");
          setCountryCode("+1");
          setPhone("");

          //await addBotMessage("Did you receive the call? (Yes or No)");
          await addBotOptions("Did you receive call from MemorialGo?", ["Yes", "No"], "callConfirmation");

          setAwaitingCallConfirmation(true); // 🔥 important
          setConversationLocked(false);
        }, 4000);
      } else {
        setConversationLocked(false); // ✅ unlock if not 200
      }
    } catch (error: any) {
      console.error("Error:", error);
      let botMessage = "Something went wrong. Please retry.";
      const status = error?.response?.status;
      if (status === 400) {
        botMessage = "Please enter a valid 10-digit phone number.";
      }
      if (status === 403) {
        botMessage =
          "You've discovered a beta feature! It's currently available to selected users, but we're working on making it available to everyone soon.";
      }
      await addBotMessage(botMessage);
      setConversationLocked(false); // ✅ unlock on error as well
    } finally {
      setLoading(false);
    }

    setPhone("");
  };
  const handleOptionSelect = async (option: string) => {
    const userTime = getCurrentTime();
    setMessages((prev) => {
      const updated = [...prev];

      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].options && !updated[i].isAnswered) {
          updated[i] = {
            ...updated[i],
            isAnswered: true,
            selectedOption: option,
          };
          break;
        }
      }

      return updated;
    });
    // Add user message bubble
    setMessages((prev) => [
      ...prev,
      { text: option, sender: "user", timestamp: userTime },
    ]);
    // Bot responds based on selection
    if (option === "Yes") {
      await addBotMessage("Thank you. We're glad we could connect with you.");
      setAwaitingCallConfirmation(false);
      setInputMode("text");
    } else if (option === "No") {
      await addBotMessage(
        "We're sorry about that. Please re-enter your number and we’ll try again.",
      );
      setCountryCode("+1");
      setPhone("");
      setInputMode("phone");
      setAwaitingCallConfirmation(false);
    }

    setCountryCode("+1");
    setPhone("");
  };

  const handleUserTextSubmit = async (text: string) => {
    const userTime = getCurrentTime();
    const normalized = text.trim().toLowerCase();
    setLoading(true);
    // Add user message to chat
    setMessages((prev) =>
      prev.map((msg) =>
        msg.options && !msg.isAnswered ? { ...msg, isAnswered: true } : msg,
      ),
    );
    setMessages((prev) => [
      ...prev,
      { text, sender: "user", timestamp: userTime },
    ]);
    setText("");
    if (awaitingCallConfirmation) {
      if (["yes", "y", "yeah", "yep"].includes(normalized)) {
        await addBotMessage("Thank you. We're glad we could connect with you.");
        setAwaitingCallConfirmation(false);
        return;
      }

      if (["no", "n", "nope"].includes(normalized)) {
        await addBotMessage(
          "We're sorry about that. Please re-enter your number and we’ll try again.",
        );
        setCountryCode("+1");
        setPhone("");
        setInputMode("phone");
        setAwaitingCallConfirmation(false);
        return;
      }

      // If they type something else → fallback to API
      setAwaitingCallConfirmation(false);
    }
    try {
      const response = await requestMessage({ message: text });
      if (response.data.enter_number === true) {
        // Switch to phone input mode
        await addBotMessage(
          response.data.reply || "Please provide your phone number.",
        );
        setInputMode("phone");
      } else {
        // Just show bot response
        await addBotMessage(response.data.reply);
      }
    } catch (err) {
      await addBotMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const initChat = async (currentSession: any) => {
    await delay(800);
    setMessages([]); // reset messages
    setJoinTime(getCurrentTime()); // set fixed join time
    if (sessionRef.current !== currentSession) return;
    await delay(800);
    if (sessionRef.current !== currentSession) return;
    await addBotMessage(`Hi, I am Amy. ${getGreeting()}`);
    if (sessionRef.current !== currentSession) return;
    await addBotMessage(`I am here to support your end-of-life care needs.`);
    if (sessionRef.current !== currentSession) return;
    await addBotMessage("Please enter your number so we can call you promptly.");
    setCountryCode("+1");
    setPhone("");
    setInputMode("phone");
    // await addBotMessage(`Are you looking for Preneed services or Immediate (At-Need) assistance?`);
    if (sessionRef.current !== currentSession) return;
    setIsInitializing(false);
    setConversationLocked(false);
  };

  const resetSearchMessage = () => {
    dispatch({
      type: "SEARCH:SET_CHAT_BOAT_MESSAGE",
      payload: {
        openChatBot: false,
        searchText: "",
      },
    });
  }
  useEffect(() => {
    if (open && !searchText && !openChatBot) {
      sessionRef.current += 1; // 🔥 new session
      const currentSession = sessionRef.current;
      setInputMode("text"); // ensure default on open

      setPhone("");
      setIsInitializing(true); // 🔥 lock immediately
      setConversationLocked(true); // 🔥 also lock conversation

      initChat(currentSession);
    }
    resetSearchMessage()
  }, [open]);

  const sendSearchedText = () => {
    setText(String(searchText));
    if (!open) {
      sessionRef.current += 1; // 🔥 new session
      setInputMode("text"); // ensure default on open
      setPhone("");
      setIsInitializing(true); // 🔥 lock immediately
      setConversationLocked(true); // 🔥 also lock conversation
      setMessages([]);
      setJoinTime(getCurrentTime()); // set fixed join time
    }
    setInputMode("text"); // ensure default on open
    handleUserTextSubmit(String(searchText));
    setIsInitializing(false);
    setConversationLocked(false);
    setOpen(true);
  };
  useEffect(() => {
    if (searchText && openChatBot && !loading) {
      sendSearchedText();
    }
    const updateTimeout = setTimeout(() => {
      resetSearchMessage();
    }, 500);

    return () => clearTimeout(updateTimeout);
  }, [searchText, openChatBot, loading]);

  // whenever messages change scroll to bottom of msgs
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);
  const groupMessages = () => {
    const groups: any[] = [];
    let currentGroup: any[] = [];

    messages.forEach((msg, index) => {
      if (currentGroup.length === 0 || currentGroup[0].sender === msg.sender) {
        currentGroup.push(msg);
      } else {
        groups.push(currentGroup);
        currentGroup = [msg];
      }

      if (index === messages.length - 1) {
        groups.push(currentGroup);
      }
    });

    return groups;
  };

  return (
    <>
      {/* Floating Bot Icon */}
      <Box className="chatbot-icon">
        <img
          src={GraceBot}
          alt="chat-bot"
          className="chatbot-img"
          style={{
            width: 62,
            height: 62,
            cursor: "pointer",
          }}
          onClick={() => setOpen(true)}
        />
      </Box>

      {/* Floating Chat Window */}
      {open && (
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Paper
            elevation={6}
            className="chatbot-window"
            sx={{
              width: {
                xs: "100%",
                sm: "26.125rem",
              },
              height: {
                xs: "100%",
                sm: "26.125rem",
              },
              right: {
                sx: "0px !important",
                lg: "1rem",
              },
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* Header */}
            <Box className="chatbot-header">
              <Box className="chatbot-header-left">
                {/* <img
                  src={GraceBot}
                  alt="chat-bot"
                  style={{
                    width: "31px",
                    marginLeft: "5px",
                  }}
                /> */}
                <Box className="chatbot-header-title">
                  <Typography
                    sx={{
                      fontWeight: 300,
                      marginLeft: "8px",
                    }}
                  >
                    Welcome to{" "}
                    <strong
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      MEMORIALGO
                    </strong>
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => {
                  setOpen(false);
                  sessionRef.current += 1;
                  setPhone("");
                  setCountryCode("+1");
                  setMessages([]);
                  setJoinTime("");
                  setJoinTime("");
                  setText(""); // reset text input
                  setInputMode("text");
                  setAwaitingCallConfirmation(false);
                }}
                className="chatbot-close-btn"
              >
                <img src={CloseIcon} alt="close-icon" />
              </IconButton>
            </Box>

            {/* Chat Body */}
            <Box className="chatbot-body" ref={chatBodyRef}>
              {joinTime && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  my={2}
                >
                  <img
                    src={GraceBot}
                    alt="Grace"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      marginBottom: 6,
                    }}
                  />
                  <Typography sx={{ fontSize: 17, fontWeight: 400 }}>
                    Amy
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    has joined the chat
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    {joinTime}
                  </Typography>
                </Box>
              )}

              {groupMessages().map((group, groupIndex) => {
                const isBotGroup = group[0].sender === "bot";

                return (
                  <Box
                    key={groupIndex}
                    display="flex"
                    flexDirection="column"
                    mb={0}
                  >
                    {group.map((msg: any, index: any) => {
                      // Show typing indicator if this message is still typing
                      if (msg.isTyping) {
                        return (
                          <Box
                            key={msg.id}
                            display="flex"
                            alignItems="center"
                            mb={1}
                          >
                            <img
                              src={GraceBot}
                              alt="Grace"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                marginRight: 8,
                              }}
                            />
                            <div className="typing-dots">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </Box>
                        );
                      }
                      return (
                        <ChatBubble
                          key={msg.id || index}
                          text={msg.text}
                          isUser={isBotGroup ? false : true}
                          options={msg.options}
                          type={msg.type}
                          isAnswered={msg.isAnswered}
                          selectedOption={msg.selectedOption} // pass options array if available
                          onOptionSelect={handleOptionSelect}
                        />
                      );
                    })}

                    {/* Grace icon + timestamp only for bot group, last message */}
                    {isBotGroup && !group.some((msg: any) => msg.isTyping) && (
                      <Box display="flex" alignItems="center" mt={1}>
                        <img
                          src={GraceBot}
                          alt="Grace"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            marginRight: 8,
                            marginTop: -7,
                            paddingTop: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "13 !important" }}
                        >
                          {group[group.length - 1].timestamp}
                        </Typography>
                      </Box>
                    )}

                    {/* Timestamp for user messages */}
                    {!isBotGroup &&
                      group.map((msg: any, index: any) => (
                        <Typography
                          key={index}
                          variant="caption"
                          sx={{
                            fontSize: "13 !important",
                            mt: 0.5,
                            alignSelf: "flex-end",
                            mr: 1.7,
                          }}
                        >
                          {msg.timestamp}
                        </Typography>
                      ))}
                  </Box>
                );
              })}
            </Box>
            {/* Footer Input */}
            <Box className="chatbot-footer">
              {inputMode === "text" ? (
                <input
                  type="text"
                  disabled={isInputDisabled}
                  placeholder="'Immediate Need' or 'Preneed Planning'"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="chat-input-field"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isSendDisabled && text.trim()) {
                      e.preventDefault();
                      handleUserTextSubmit(text);
                    }
                  }}
                  style={{
                    width: "100%",
                    marginLeft: "10px",
                    padding: "8px 10px",
                    fontSize: "15px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              ) : (
                <NewPhoneNumberComponent
                  value={phone}
                  placeholder="Enter your phone number"
                  onChange={setPhone}
                  countryCode={countryCode}
                  onCountryChange={setCountryCode}
                  maskDigits={6}
                  disabled={isInputDisabled}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !isSendDisabled &&
                      phone.length === 10
                    ) {
                      handleSend();
                    }
                  }}
                />
              )}
              <Box
                className={`send-button ${isSendDisabled ? "disabled" : ""}`}
                onClick={() => {
                  if (isSendDisabled) return; // 🔥 prevents duplicate click

                  if (inputMode === "text") {
                    handleUserTextSubmit(text.trim());
                  } else if (inputMode === "phone") {
                    handleSend();
                  }
                }}
                sx={{
                  cursor: isSendDisabled ? "default" : "pointer",
                }}
              >
                <img
                  src={isSendDisabled ? SendInactiveIcon : SendActiveIcon}
                  alt="send-btn"
                />
              </Box>
            </Box>
          </Paper>

          {/* Triangle Pointer */}
          <img
            src={TriangleIcon}
            alt="triangle-shadow"
            style={{
              position: "absolute",
              bottom: "103px",
              right: "45px",
              zIndex: 99,
              width: "4.438rem",
            }}
          />
        </Box>
      )}
    </>
  );
}
